import React from 'react';
import { Badge } from '@oysterjs/ui/badge';
import { type ColumnDef, DataTable } from '@oysterjs/ui/data-table';
import { SegmentedControl, SegmentedControlOption } from '@oysterjs/ui/segmented-control';
import { Spinner } from '@oysterjs/ui/spinner';
import { TableHeader } from '@oysterjs/ui/table';

import type { BulkDraft, BulkDraftEmail } from '~/api/data/email-bulk-send/queries';
import { Dash } from '~/components/clients/clients-columns';
import { FieldReferenceText } from '~/components/email-templates/field-reference-text';
import { OutboxStatusBadge } from '~/components/mailbox/outbox-columns';
import {
  type BulkDraftRecipient,
  bulkDraftRecipientStatus,
  hasDraftedEmail,
  isReadyToSend
} from './bulk-draft-recipient-status';

/** Keeps a cell's text on one line, so a long value doesn't make its row taller. */
const ONE_LINE_CELL_CLASS = 'whitespace-nowrap';

type RecipientsTab = 'all' | 'heldBack';

const EMPTY_TAB_MESSAGE: Record<RecipientsTab, string> = {
  all: 'No recipients yet.',
  heldBack: 'No emails have issues.'
};

type RecipientRow = { recipient: BulkDraftRecipient; number: number };

const isNotReadyToSend = (recipient: BulkDraftRecipient) => !isReadyToSend(recipient);

const RecipientStatus = ({ recipient }: { recipient: BulkDraftRecipient }) => {
  switch (bulkDraftRecipientStatus(recipient)) {
    case 'drafting':
      return (
        <Badge color="blue">
          <Spinner size="size-3.5" color="blue" />
          Drafting
        </Badge>
      );
    case 'ready':
      return <OutboxStatusBadge status="composed" />;
    case 'heldBack':
      return (
        <Badge color="amber" title={recipient.heldBackReason ?? undefined}>
          Issue
          {recipient.heldBackReason && (
            <span className="sr-only">. {recipient.heldBackReason}</span>
          )}
        </Badge>
      );
    case 'couldNotDraft':
      return <Badge color="red">Couldn&apos;t draft this email</Badge>;
    case 'notDrafted':
      return <Badge color="neutral">Not drafted</Badge>;
  }
};

const templateColumn = (
  emailByArtifactId: Record<string, BulkDraftEmail>,
  templateNameById: Record<string, string>
): ColumnDef<RecipientRow> => ({
  id: 'template',
  header: 'Template',
  cellClassName: ONE_LINE_CELL_CLASS,
  renderCell: ({ recipient }) => {
    if (!hasDraftedEmail(recipient)) {
      return <Dash />;
    }
    // An email whose read failed is unknown, not one without a template.
    const email = emailByArtifactId[recipient.artifactId ?? ''];
    const templateName = email?.templateId ? templateNameById[email.templateId] : email && 'None';
    return templateName ? (
      <span
        className="block max-w-28 truncate text-neutral-700 dark:text-neutral-300"
        title={templateName}
      >
        {templateName}
      </span>
    ) : (
      <Dash />
    );
  }
});

/** "+ Cc", "+ Bcc" or "+ Cc, Bcc" for an email that copies anyone else in; null otherwise. */
const copiesHint = (email: BulkDraftEmail | undefined) => {
  const copiedAs = [email?.hasCc && 'Cc', email?.hasBcc && 'Bcc'].filter(Boolean);
  return copiedAs.length > 0 ? `+ ${copiedAs.join(', ')}` : null;
};

/**
 * The address, with the name and who else is copied in on a muted line under it. Every To cell
 * is two lines high, so rows stay the same height whether or not they have a name.
 */
const RecipientTo = ({
  recipient,
  email
}: {
  recipient: BulkDraftRecipient;
  email: BulkDraftEmail | undefined;
}) => {
  const nameNote =
    recipient.name && `${recipient.name}${recipient.amsAccountId ? ' · from your AMS' : ''}`;
  const copies = hasDraftedEmail(recipient) ? copiesHint(email) : null;
  const notesLine = [nameNote, copies].filter(Boolean).join(' · ');
  return (
    <div className="flex h-9 max-w-48 flex-col justify-center">
      {recipient.email ? (
        <span className="truncate" title={recipient.email}>
          {recipient.email}
        </span>
      ) : (
        <Dash />
      )}
      {notesLine && (
        <span className="truncate text-xs text-neutral-500 dark:text-neutral-400" title={notesLine}>
          {notesLine}
        </span>
      )}
    </div>
  );
};

// The text columns' max widths (To, Subject, Status at 12-14rem) keep the table, Open and Remove
// included, inside the ~1000px card a 1366px screen leaves, so nothing scrolls sideways at lg.
const recipientColumns = (opts: {
  emailByArtifactId: Record<string, BulkDraftEmail>;
  /** Names each email's template in a column of its own, when they differ. */
  templateNameById: Record<string, string> | null;
  rowActions: (recipient: BulkDraftRecipient) => React.ReactNode;
  sentStatus?: (recipient: BulkDraftRecipient) => React.ReactNode;
}): ColumnDef<RecipientRow>[] => [
  {
    id: 'number',
    header: '#',
    cellClassName: `${ONE_LINE_CELL_CLASS} text-neutral-500 dark:text-neutral-400`,
    renderCell: ({ number }) => number
  },
  {
    id: 'to',
    header: 'To',
    cellClassName: ONE_LINE_CELL_CLASS,
    renderCell: ({ recipient }) => (
      <RecipientTo
        recipient={recipient}
        email={opts.emailByArtifactId[recipient.artifactId ?? '']}
      />
    )
  },
  {
    id: 'subject',
    header: 'Subject',
    cellClassName: ONE_LINE_CELL_CLASS,
    renderCell: ({ recipient }) => {
      const subject = hasDraftedEmail(recipient)
        ? opts.emailByArtifactId[recipient.artifactId ?? '']?.subject
        : undefined;
      return subject ? (
        <span
          className="block max-w-56 truncate text-neutral-700 dark:text-neutral-300"
          title={subject}
        >
          <FieldReferenceText text={subject} />
        </span>
      ) : (
        <Dash />
      );
    }
  },
  ...(opts.templateNameById ? [templateColumn(opts.emailByArtifactId, opts.templateNameById)] : []),
  {
    id: 'status',
    header: 'Status',
    cellClassName: ONE_LINE_CELL_CLASS,
    renderCell: ({ recipient }) =>
      opts.sentStatus ? opts.sentStatus(recipient) : <RecipientStatus recipient={recipient} />
  },
  {
    id: 'actions',
    renderHeader: () => (
      <TableHeader>
        <span className="sr-only">Actions</span>
      </TableHeader>
    ),
    align: 'right',
    cellClassName: ONE_LINE_CELL_CLASS,
    noHref: true,
    renderCell: ({ recipient }) => opts.rowActions(recipient)
  }
];

/** The bulk draft's recipients and where each one's email stands, by tab. */
export const BulkDraftRecipients = ({
  bulkDraft,
  emailByArtifactId,
  templateNameById,
  rowActions,
  sentStatus,
  isHeldBack = isNotReadyToSend,
  scrollTopRef
}: {
  bulkDraft: BulkDraft;
  emailByArtifactId: Record<string, BulkDraftEmail>;
  /** Names each email's template in a column of its own; null when they share one. */
  templateNameById: Record<string, string> | null;
  /** What the agent can do with one recipient's row. */
  rowActions: (recipient: BulkDraftRecipient) => React.ReactNode;
  /** Where each recipient's email stands once sent, in place of how its drafting went. */
  sentStatus?: (recipient: BulkDraftRecipient) => React.ReactNode;
  /** Who the Held back tab lists: by default, everyone Send would leave out. */
  isHeldBack?: (recipient: BulkDraftRecipient) => boolean;
  /** Where the table is scrolled to, kept here so a table that replaces this one opens there. */
  scrollTopRef?: React.MutableRefObject<number>;
}) => {
  const [tab, setTab] = React.useState<RecipientsTab>('all');
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  // The ref is the page's own, so this runs as the table mounts; after that the agent scrolls it.
  React.useLayoutEffect(() => {
    if (scrollerRef.current && scrollTopRef) {
      scrollerRef.current.scrollTop = scrollTopRef.current;
    }
  }, [scrollTopRef]);

  const rows = bulkDraft.recipients.map((recipient, index) => ({ recipient, number: index + 1 }));
  const rowsByTab: Record<RecipientsTab, RecipientRow[]> = {
    all: rows,
    heldBack: rows.filter(({ recipient }) => isHeldBack(recipient))
  };
  const tabRows = rowsByTab[tab];
  const columns = React.useMemo(
    () =>
      recipientColumns({
        emailByArtifactId,
        templateNameById,
        rowActions,
        sentStatus
      }),
    [emailByArtifactId, templateNameById, rowActions, sentStatus]
  );

  return (
    <>
      <SegmentedControl
        value={tab}
        onChange={setTab}
        aria-label="Which emails to show"
        className="mb-3 sm:max-w-md"
      >
        <SegmentedControlOption value="all">All {rowsByTab.all.length}</SegmentedControlOption>
        <SegmentedControlOption value="heldBack">
          Issues {rowsByTab.heldBack.length}
        </SegmentedControlOption>
      </SegmentedControl>
      {/* Fills the rest of the page from lg up and scrolls the table both ways; its header row
          sticks to the top. On a short screen the page scrolls before the table gets too small. */}
      <div
        ref={scrollerRef}
        onScroll={(event) => {
          if (scrollTopRef) scrollTopRef.current = event.currentTarget.scrollTop;
        }}
        className="min-h-0 flex-1 overflow-auto lg:min-h-64 rounded-lg bg-white px-4 pb-2 shadow sm:px-6 dark:bg-neutral-800"
      >
        {tabRows.length === 0 ? (
          <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {EMPTY_TAB_MESSAGE[tab]}
          </p>
        ) : (
          <DataTable
            data={tabRows}
            columns={columns}
            keyExtractor={({ recipient }) => recipient.id}
            stickyHeader
            tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
          />
        )}
      </div>
    </>
  );
};
